import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { CharacterRole } from 'app/frontend/types/custom'
import capitalize from 'lodash/capitalize'
import RosterMember from './RosterMember'
import TeamMemberContextMenu from './TeamMemberContextMenu'
import UserBnetConnection from './UserBnetConnection'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { MessageCircleMore } from 'lucide-react'
import { useContext, useMemo, useState } from 'react'
import MemberNoteModal from './MemberNoteModal'
import ProtectedContent, {
  hasAccessToContent,
} from 'app/frontend/base/components/ProtectedContent'
import { useTeam } from 'app/frontend/base/config/context/team'
import { RosterSetupContext } from './RosterSetup'
import { Badge } from '@/components/ui/badge'
import Card from 'app/frontend/base/components/Card'
import ItemLevelBadge from './ItemLevelBadge'

type RoleBlockProps = {
  role: CharacterRole
  compact?: boolean
  className?: string
}

const RoleBlock = ({ role, compact = false, className }: RoleBlockProps) => {
  const { guild, team } = useTeam()
  const [editingNoteFor, setEditingNoteFor] =
    useState<wowaudit.TeamMembership>()

  const {
    mainsByRole,
    altsByRole,
    reservesByRole,
    offSpecsByRole,
    charactersById,
    mainByUser,
    usersById,
    userIdsWithDuplicateMains,
  } = useContext(RosterSetupContext)

  const { hasAccess: hasAdminAccess } = hasAccessToContent(
    'admin',
    'roster',
    guild,
    team,
  )

  const mains = (mainsByRole[role] ?? []).filter(
    (member) => member.teamRank.player_type === 'main',
  )
  const reserves = reservesByRole[role] ?? []
  const alts = altsByRole[role] ?? []
  const offspecs = useMemo(() => {
    const seen = new Set<number>()
    return (offSpecsByRole[role] ?? [])
      .filter((item) => {
        if (seen.has(item.member.id)) return false
        seen.add(item.member.id)
        return true
      })
      .map((item) => item.member)
  }, [offSpecsByRole, role])

  const itemLevel = (member: wowaudit.TeamMembership, compact?: boolean) => {
    const details = charactersById?.[member.characterReference.id]

    return <ItemLevelBadge details={details} size={compact ? 'sm' : 'md'} />
  }

  const noteAction = (
    member: wowaudit.TeamMembership,
    className = 'flex items-center justify-center p-2 md:p-1 shrink',
  ) => (
    <Tooltip key={member.id}>
      <TooltipTrigger
        onClick={() => (hasAdminAccess ? setEditingNoteFor(member) : null)}
        className={className}
      >
        <MessageCircleMore
          size={14}
          className={member.note ? '' : 'text-muted-foreground/20'}
        />
      </TooltipTrigger>
      <TooltipContent className='max-w-[50vw] whitespace-normal'>
        {member.note || (
          <span className='opacity-70 italic'>
            {hasAdminAccess ? 'Click to add note' : 'No note'}
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  )

  const actions = (member: wowaudit.TeamMembership) => {
    return (
      <>
        {noteAction(member)}

        <ProtectedContent
          section='roster'
          requirement='admin'
          renderUnauthorized={false}
        >
          <TeamMemberContextMenu member={member} size={'small'} />
        </ProtectedContent>
      </>
    )
  }

  return (
    <Card key={role} className={className}>
      {editingNoteFor && (
        <MemberNoteModal
          onClose={() => setEditingNoteFor(undefined)}
          member={editingNoteFor}
        />
      )}

      <h3 className='flex items-center gap-2 justify-between border-b pb-2 mb-2'>
        <div className='flex items-center gap-3'>
          <CharacterRoleIcon role={role} size='large' />
          {capitalize(role)}
        </div>

        <Badge variant='outline' className='text-base'>
          {mains.length}
        </Badge>
      </h3>
      <div className='flex flex-col gap-1'>
        {mains.length === 0 ? (
          <p className='italic text-muted-foreground'>
            No main members with this role
          </p>
        ) : (
          mains.map((member) => (
            <RosterMember
              key={member.id}
              member={member}
              className={`${
                member.characterReference.gdprStatus === 'exists'
                  ? ''
                  : 'bg-destructive hover:bg-destructive'
              } py-1 px-2 gap-1`}
            >
              <div className='flex items-center justify-end gap-0.5'>
                {itemLevel(member)}

                {!compact && (
                  <>
                    <UserBnetConnection
                      member={member}
                      usersById={usersById}
                      userIdsWithDuplicateMains={userIdsWithDuplicateMains}
                      className='border rounded-lg mr-0.5'
                      size='small'
                    />
                    {actions(member)}
                  </>
                )}
              </div>
            </RosterMember>
          ))
        )}
      </div>

      {reserves.length > 0 ? (
        <>
          <h6 className='text-sm mt-4 mb-2 pb-1 border-b flex justify-between items-center'>
            <span>Reserves</span>
            <Badge variant='outline' className='text-sm'>
              {reserves.length}
            </Badge>
          </h6>
          <div className='grid grid-cols-2 gap-1'>
            {reserves.map((member) => (
              <Card
                key={member.id}
                className={`text-xs ${
                  member.characterReference.gdprStatus === 'exists'
                    ? ''
                    : 'bg-destructive hover:bg-destructive'
                } py-0.5 px-2 flex flex-col`}
              >
                <RosterMember
                  member={member}
                  compact={true}
                  className='border-0 p-0 bg-transparent hover:bg-transparent'
                >
                  {compact ? (
                    <div>{itemLevel(member, true)}</div>
                  ) : (
                    <ProtectedContent
                      section='roster'
                      requirement='admin'
                      renderUnauthorized={false}
                    >
                      <div className='ml-1'>
                        <TeamMemberContextMenu member={member} size={'small'} />
                      </div>
                    </ProtectedContent>
                  )}
                </RosterMember>

                {!compact && (
                  <div className='flex gap-1 justify-between items-center mt-0.5'>
                    <UserBnetConnection
                      member={member}
                      usersById={usersById}
                      userIdsWithDuplicateMains={userIdsWithDuplicateMains}
                      className='border rounded-lg h-5 px-0.5 py-0 scale-90 origin-left'
                      size='small'
                    />
                    <div className='flex items-center gap-0.5'>
                      {noteAction(
                        member,
                        'flex items-center justify-center p-0.5 shrink',
                      )}
                      {itemLevel(member, true)}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </>
      ) : null}

      {alts.length > 0 ? (
        <>
          <h6 className='text-sm mt-4 mb-2 pb-1 border-b flex justify-between items-center'>
            <span>Alts</span>
            <Badge variant='outline' className='text-sm'>
              {alts.length}
            </Badge>
          </h6>
          <div className='grid grid-cols-2 gap-1'>
            {alts.map((member) => {
              const main = mainByUser?.[member.characterReference.userId ?? '']

              return (
                <Card
                  key={member.id}
                  className={`text-xs ${
                    member.characterReference.gdprStatus === 'exists'
                      ? ''
                      : 'bg-destructive hover:bg-destructive'
                  } py-0.5 px-2 flex flex-col`}
                >
                  <RosterMember
                    member={member}
                    compact={true}
                    className='border-0 p-0 bg-transparent hover:bg-transparent'
                  >
                    {compact ? (
                      <div>{itemLevel(member, true)}</div>
                    ) : (
                      <div className='flex items-center justify-end gap-0.5'>
                        {actions(member)}
                      </div>
                    )}
                  </RosterMember>

                  <div className='flex gap-1 justify-between'>
                    <div className='text-[11px] opacity-70 leading-tight mt-0.5 truncate'>
                      {main?.characterReference.name ? (
                        <span>Alt of {main.characterReference.name}</span>
                      ) : (
                        <span className='italic text-warning-foreground'>
                          No main found
                        </span>
                      )}
                    </div>

                    {!compact && <div>{itemLevel(member, true)}</div>}
                  </div>
                </Card>
              )
            })}
          </div>
        </>
      ) : null}

      {offspecs.length > 0 ? (
        <>
          <h6 className='text-sm mt-4 mb-2 pb-1 border-b flex justify-between items-center'>
            <span>Off specs</span>
            <Badge variant='outline' className='text-sm'>
              {offspecs.length}
            </Badge>
          </h6>
          <div className='flex gap-1 flex-wrap'>
            {offspecs.map((member) => (
              <RosterMember
                key={member.id}
                member={member}
                compact={true}
                className='text-xs py-0.5 px-2'
                customSpecDetails={member.offspecDetails.filter(
                  (offspec) => offspec.role === role,
                )}
              >
                {!compact && (
                  <ProtectedContent
                    section='roster'
                    requirement='admin'
                    renderUnauthorized={false}
                  >
                    <div className='ml-1'>
                      <TeamMemberContextMenu member={member} size={'small'} />
                    </div>
                  </ProtectedContent>
                )}
              </RosterMember>
            ))}
          </div>
        </>
      ) : null}
    </Card>
  )
}

export default RoleBlock
